// Affiliate landing-page (lp) attribution persistence.
//
// Goal: when a customer arrives via ?lp=<slug>, keep that attribution as they
// browse from one product to another, and carry it through login so they land
// back on the same landing page.
//
// Strategy (last-touch attribution):
//   1. Capture: if the URL has ?lp=<value>, (re)write it to a session-only
//      first-party cookie. A newer affiliate link overrides an older one
//      (last touch wins). Session-only because LP influence is same-session;
//      the app-side webhook applies the TIER tag at account creation, so
//      persistence beyond the session isn't needed.
//   2. Decorate product links: on every page, append ?lp=<value> to internal
//      product links (/products/...) using whatever attribution we currently
//      hold (URL or cookie). Non-product links (home, collections, cart,
//      checkout) are left untouched, so lp only ever appears on product URLs.
//   3. Decorate login links: rewrite return_to=<current URL with ?lp=> so the
//      customer lands back on the same LP after OTP login — critical because
//      the LP context drives the tier tag applied at account creation.
//
// The cookie stores only the campaign slug (e.g. "luke") — no PII.

(function () {
  var COOKIE_NAME = 'calroy_lp';

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function writeCookie(name, value) {
    // Session cookie — no expires / Max-Age. Dies with the browser session.
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; SameSite=Lax';
  }

  // 1. Capture / refresh from the URL (last touch).
  var urlLp = (new URLSearchParams(window.location.search).get('lp') || '').trim();
  if (urlLp) {
    writeCookie(COOKIE_NAME, urlLp);
  }

  // 2. Decorate links from whatever attribution we currently hold.
  var lp = urlLp || readCookie(COOKIE_NAME);
  if (!lp) return;

  // Build the return_to for login links: current URL, guaranteed to carry lp.
  var currentUrlWithLp = (function () {
    var u = new URL(window.location.href);
    u.searchParams.set('lp', lp);
    return u.pathname + u.search + u.hash;
  })();

  var SELECTOR =
    'a[href*="/products/"], a[href*="/customer_authentication/login"], a[href*="/account/login"]';

  function decorateLink(a) {
    var href = a.getAttribute('href');
    if (!href) return;

    var url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return;
    }

    // Same-origin only.
    if (url.origin !== window.location.origin) return;

    // Login links: preserve any existing return_to (which may carry role hints
    // like ?hcp=true for the practitioner portal) and add lp to ITS query
    // string. If no return_to is set, point at the current URL with lp. Both
    // new customer accounts (/customer_authentication/login) and classic
    // (/account/login) honor return_to as a relative URL.
    if (
      url.pathname.indexOf('/customer_authentication/login') !== -1 ||
      url.pathname.indexOf('/account/login') !== -1
    ) {
      var existing = url.searchParams.get('return_to');
      var target;
      if (existing) {
        var existingUrl;
        try {
          existingUrl = new URL(existing, window.location.origin);
        } catch (e) {
          return;
        }
        if (existingUrl.searchParams.get('lp') === lp) return; // already carries lp
        existingUrl.searchParams.set('lp', lp);
        target = existingUrl.pathname + existingUrl.search + existingUrl.hash;
      } else {
        target = currentUrlWithLp;
      }
      if (existing === target) return;
      url.searchParams.set('return_to', target);
      a.setAttribute('href', url.pathname + url.search + url.hash);
      return;
    }

    // Product links: append lp, don't clobber an existing one.
    if (url.pathname.indexOf('/products/') === -1) return;
    if (url.searchParams.get('lp')) return;
    url.searchParams.set('lp', lp);
    a.setAttribute('href', url.pathname + url.search + url.hash);
  }

  function decorateWithin(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.matches && node.matches(SELECTOR)) decorateLink(node);
    if (node.querySelectorAll) {
      node.querySelectorAll(SELECTOR).forEach(decorateLink);
    }
  }

  function decorateAll() {
    decorateWithin(document.body || document.documentElement);
  }

  // Re-decorate links added, replaced, or patched after load. The theme uses
  // view transitions / morph and section hydration (e.g. the header re-hydrates
  // itself on load), so the header menu, mega-menu, drawer and section content
  // can be (re)rendered after the initial pass. Crucially, morph patches the
  // href attribute of existing <a> elements in place, resetting it to the
  // server value (without ?lp=) — so we watch both childList (new nodes) and
  // the href attribute (patched nodes). No infinite loop: once a link carries
  // lp, decorateLink() returns early, so our own rewrite doesn't re-trigger.
  function startObserver() {
    if (!window.MutationObserver) return;
    var target = document.body || document.documentElement;
    if (!target) return;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes') {
          decorateLink(m.target);
          continue;
        }
        var added = m.addedNodes;
        for (var j = 0; j < added.length; j++) {
          decorateWithin(added[j]);
        }
      }
    }).observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    });
  }

  function init() {
    decorateAll();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
